<template>
    <div class="circular-progress-container">
        <svg
            class="circular-progress"
            :width="size"
            :height="size"
            viewBox="0 0 100 100"
        >
            <!-- Background circle -->
            <circle
                class="progress-bg"
                cx="50"
                cy="50"
                :r="radius"
                fill="none"
                :stroke-width="strokeWidth"
            />
            <!-- Progress circle -->
            <circle
                class="progress-bar"
                cx="50"
                cy="50"
                :r="radius"
                fill="none"
                :stroke-width="strokeWidth"
                :stroke-dasharray="circumference"
                :stroke-dashoffset="dashOffset"
                :style="{ stroke: progressColor }"
            />
        </svg>
        <div
            class="progress-text"
            :style="{ backgroundColor: progressColor }"
        >
            {{ Math.round(percentage) }}%
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
    percentage: number
    size?: number
    strokeWidth?: number
    color?: string
}

const props = withDefaults(defineProps<Props>(), {
    size: 43,
    strokeWidth: 14,
    color: '#4CAF50'
})

const radius = computed(() => (100 - props.strokeWidth) / 2)
const circumference = computed(() => 2 * Math.PI * radius.value)
const dashOffset = computed(() => {
    const progress = Math.min(Math.max(props.percentage, 0), 100)
    return circumference.value - (progress / 100) * circumference.value
})

const progressColor = computed(() => {
    // Change color based on progress
    if (props.percentage < 30) return '#f44336' // Red for low progress
    if (props.percentage < 70) return '#ff9800' // Orange for medium progress
    return props.color
})
</script>

<style scoped lang="scss">
.circular-progress-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.circular-progress {
    transform: rotate(-90deg);
}

.progress-bg {
    stroke: color-mix(in srgb, var(--primary-actions) 10%, transparent);
}

.progress-bar {
    transition: stroke-dashoffset 0.5s ease, stroke 0.3s ease;
    stroke-linecap: round;
}

.progress-text {
    padding: 4px 8px;
    margin-top: auto;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    color: var(--white);
    transition: background-color 0.3s ease;
}
</style>
